import { tipAttrs } from "../../tooltip";
import { type Combatant, type HpHiddenCombatant, OVERSHIELD_RESOURCE } from "@lancer-vtt/domain";
import type { GlossaryKey } from "../../glossary/combatGlossary";
import { InfoTerm, Term } from "../../glossary/Term";
import { isHpVisible } from "../disclosure";
import "./unit-sheet.css";

/**
 * The unit sheet's track labels and the glossary term each opens (`combat-glossary` 3.3). One table,
 * so the sheet's labels and the coverage pin (`combatGlossary.test.ts`) read the same strings.
 */
export const MECH_TRACK_LABELS = {
  hp: { label: "HP", term: "stat:hp" },
  heat: { label: "HEAT", term: "stat:heat" },
  structure: { label: "Struct", term: "stat:structure" },
  stress: { label: "Stress", term: "stat:stress" },
  repair: { label: "Repair", term: "stat:repair_cap" },
} as const satisfies Record<string, { label: string; term: GlossaryKey }>;

/** A track's current value over its max. */
export type TrackValue = { value: number; max: number };

/**
 * A pack-authored **pool** a talent keeps in `current.resources` and spends from, with its sheet
 * label and cap. No other surface renders a custom resource, so a pool the table must track
 * belongs here (`ll1-granted-reactions` 11.1).
 */
const RESOURCE_POOLS = [
  { key: "blademaster_dice", label: "BLADEMASTER", max: 3, title: "Blademaster dice (Duelist 2): spent on Parry, Deflect, Feint and Trip; gained on a Main Melee hit, 1/round" },
] as const;

/** A pool chip: its label, the count held, its cap and the hover text. */
export type ResourcePool = { label: string; value: number; max: number; title: string };

/** The pools `resources` holds, in registry order. A pool never gained is absent, not shown as 0. */
export function resourcePools(resources: Readonly<Record<string, number>> | undefined): ResourcePool[] {
  return RESOURCE_POOLS.flatMap((p) => (resources?.[p.key] === undefined ? [] : [{ label: p.label, value: resources[p.key]!, max: p.max, title: p.title }]));
}

/**
 * The unit sheet's **vitals block** (`unit-sheet-redesign` 3.1): the HP and HEAT bars, then one pips
 * row carrying STRUCT and STRESS pips, REPAIR n / m, and the OVERSHIELD and BURN chips. The bars sit
 * side by side when the sheet is 560px or wider and stack below that (a container query on
 * `.unit-sheet`). Every part but HP is optional, so one block serves a mech, an NPC with or without
 * a heat track, and a pilot on foot.
 *
 * Each body builds the props from its own combatant; this block only lays them out.
 */
export function UnitSheetVitals({
  hp,
  maxHp,
  heat,
  exposed,
  structure,
  stress,
  repair,
  shield,
  burn,
  pools = [],
  onSetTrack,
  disabled,
}: {
  /** Current HP, or `null` when the viewer may not see it (`showEnemyHp` off, `decisions/0082`). */
  hp: number | null;
  maxHp: number;
  /** Absent for a unit with no heat track (a pilot, a heatless NPC): no HEAT bar, never a faked one. */
  heat?: TrackValue;
  /** The unit carries Exposed: the HEAT fill takes the accent tone. The status chip says the rest. */
  exposed: boolean;
  structure?: TrackValue;
  stress?: TrackValue;
  /** Repair Cap remaining over its frozen max — a mech's. */
  repair?: TrackValue;
  /** Overshield — display-only, a chip only when non-zero. */
  shield: number;
  /** Marked burn — display-only, a chip only when non-zero. */
  burn: number;
  /** Talent pools ({@link resourcePools}) — display-only chips. */
  pools?: readonly ResourcePool[];
  /**
   * The −/+ correction steppers' handler (`set_hp` / `set_heat`), or `null` for a viewer who
   * neither controls the unit nor is the GM: their sheet shows the bars with no steppers at all.
   */
  onSetTrack: ((kind: "hp" | "heat", value: number) => void) | null;
  /** The steppers show but are inert (no seat yet). */
  disabled: boolean;
}) {
  const setHp = onSetTrack && ((n: number) => onSetTrack("hp", n));
  const setHeat = onSetTrack && ((n: number) => onSetTrack("heat", n));
  return (
    <div className={`unit-sheet-vitals${heat ? "" : " is-single"}`}>
      {hp === null ? (
        <SheetVital label={MECH_TRACK_LABELS.hp.label} term={MECH_TRACK_LABELS.hp.term} track={null} max={maxHp} tone="hp" onSet={null} disabled />
      ) : (
        <SheetVital label={MECH_TRACK_LABELS.hp.label} term={MECH_TRACK_LABELS.hp.term} track={{ value: hp, max: maxHp }} max={maxHp} tone="hp" onSet={setHp} disabled={disabled} />
      )}
      {heat && (
        <SheetVital
          label={MECH_TRACK_LABELS.heat.label}
          term={MECH_TRACK_LABELS.heat.term}
          info="card:heat"
          track={heat}
          max={heat.max}
          tone={exposed ? "exposed" : "heat"}
          onSet={setHeat}
          disabled={disabled}
        />
      )}
      <PipsRow structure={structure} stress={stress} repair={repair} shield={shield} burn={burn} pools={pools} />
    </div>
  );
}

/**
 * An NPC's {@link UnitSheetVitals} tracks, off the GM's view or a player's `"full"`-disclosed one.
 * HP is `null` when the projection stripped it (`showEnemyHp` off). HEAT only when
 * `base.maxHeat` is set, never a faked bar. STRUCT and STRESS default to 1 of 1 and to full for a
 * blob predating the tracks; STRESS only when `base.maxStress` is set.
 */
export function npcVitalTracks(c: Combatant | HpHiddenCombatant) {
  const cur = c.current;
  const maxStruct = c.base.maxStructure ?? 1;
  const maxStress = c.base.maxStress;
  return {
    hp: isHpVisible(cur) ? cur.hp : null,
    maxHp: c.base.maxHp,
    heat: c.base.maxHeat !== undefined ? { value: cur.heat ?? 0, max: c.base.maxHeat } : undefined,
    exposed: c.statuses.some((s) => s.name.toLowerCase() === "exposed"),
    structure: { value: cur.structure ?? maxStruct, max: maxStruct },
    stress: maxStress !== undefined ? { value: cur.stress ?? maxStress, max: maxStress } : undefined,
    shield: cur.resources?.[OVERSHIELD_RESOURCE] ?? 0,
    burn: cur.burn ?? 0,
  };
}

/** The hover text on a masked hostile's vitals: what would reveal them. */
export const MASKED_VITALS_TITLE = "Hidden until the GM reveals this unit or it is Scanned";

/**
 * The vitals block on a masked hostile (`"class"` or `"loadout"` disclosure, where the projection
 * carries no `current` and no stat block): HP and HEAT as `?? / ??` on hatched bars, and STRUCT and
 * STRESS as one hatched `??` box each rather than a row of pips, since a pip count would state the
 * max the projection withholds. No steppers, no chips.
 */
export function MaskedSheetVitals() {
  return (
    <div className="unit-sheet-vitals is-masked">
      <SheetVital label={MECH_TRACK_LABELS.hp.label} term={MECH_TRACK_LABELS.hp.term} track={null} max={null} tone="hp" onSet={null} disabled hiddenTitle={MASKED_VITALS_TITLE} />
      <SheetVital label={MECH_TRACK_LABELS.heat.label} term={MECH_TRACK_LABELS.heat.term} info="card:heat" track={null} max={null} tone="heat" onSet={null} disabled hiddenTitle={MASKED_VITALS_TITLE} />
      <div className="unit-sheet-pips">
        {([MECH_TRACK_LABELS.structure, MECH_TRACK_LABELS.stress] as const).map((t) => (
          <span key={t.label} className="unit-sheet-pip-group">
            <span className="unit-sheet-label">
              <Term k={t.term}>{t.label}</Term>
            </span>
            <span className="unit-sheet-pip-hidden" aria-label={`${t.label.toLowerCase()} hidden`} title={MASKED_VITALS_TITLE}>
              ??
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * One vital bar: a mono label and `value / max` over a 2px-bordered fill bar, with the 18px −/+
 * correction steppers either side when `onSet` is given. The steppers dispatch the authoritative
 * `set_hp` / `set_heat` (the server clamps and the reconcile snaps the display), so the bar is a
 * mirror with no local state. The fill reads the accent below half HP, the warn tone for heat, and
 * the accent once Exposed.
 *
 * `track: null` is a hidden value: `?? / max` on a hatched bar and no steppers, ever. A viewer who
 * can't see a number can't usefully correct it. On a masked hostile the max is hidden too (`max:
 * null`, `?? / ??`).
 */
export function SheetVital({
  label,
  term,
  info,
  track,
  max,
  tone,
  onSet,
  disabled,
  hiddenTitle = "Hidden — enable Show Enemy HP to reveal",
}: {
  label: string;
  /** The label's glossary term (`combat-glossary`). */
  term?: GlossaryKey;
  /** A visible (i) beside the label opening this explainer (`card:heat` on every heat track, `heat-info-card`). */
  info?: GlossaryKey;
  track: TrackValue | null;
  /** `null` only with `track: null`: the viewer may see neither the value nor its max. */
  max: number | null;
  tone: "hp" | "heat" | "exposed";
  onSet: ((n: number) => void) | null;
  disabled: boolean;
  /** The hover text on a hidden value, saying what would reveal it. */
  hiddenTitle?: string;
}) {
  const value = track?.value ?? 0;
  const pct = track && track.max > 0 ? Math.max(0, Math.min(100, (value / track.max) * 100)) : 0;
  const low = tone === "hp" && track !== null && value <= track.max / 2;
  const fillClass = tone === "exposed" || low ? "is-accent" : tone === "heat" ? "is-heat" : "is-ink";
  return (
    <div className={`unit-sheet-vital${track ? "" : " is-hidden"}`} data-vital={label.toLowerCase()} title={track ? undefined : hiddenTitle}>
      <div className="unit-sheet-vital-head">
        <span className="unit-sheet-vital-name">
          <span className="unit-sheet-label">{term ? <Term k={term}>{label}</Term> : label}</span>
          {info && <InfoTerm k={info} label={`About ${label.toLowerCase()}`} />}
        </span>
        <span className="unit-sheet-vital-num">
          {track ? value : "??"} <small>/ {max ?? "??"}</small>
        </span>
      </div>
      <div className="unit-sheet-vital-ctl">
        {track && onSet && (
          <button type="button" className="unit-sheet-step" disabled={disabled || value <= 0} aria-label={`decrement ${label}`} {...tipAttrs({ label: `${label} −1` })} onClick={() => onSet(value - 1)}>
            −
          </button>
        )}
        <div className={`unit-sheet-bar${track ? "" : " is-hatched"}`} aria-hidden>
          {track && <div className={`unit-sheet-bar-fill ${fillClass}`} style={{ width: `${pct}%` }} />}
        </div>
        {track && onSet && (
          <button type="button" className="unit-sheet-step" disabled={disabled || value >= track.max} aria-label={`increment ${label}`} {...tipAttrs({ label: `${label} +1` })} onClick={() => onSet(value + 1)}>
            +
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The pips row under the bars. Renders nothing when there is nothing to show (a pilot on foot with no
 * burn), so a quiet unit's sheet carries no empty row.
 *
 * **OVERSHIELD and BURN are chips, never bars or pips against a max.** Overshield's granted value
 * (keep-highest, `overshield.ts`'s `grantOvershieldStep`) has no separate max. Burn is a *mark*, not
 * a track: RAW burn is damage on a timer (at the END of the marked unit's turn an ENGINEERING check
 * vs 10 clears it, or it deals the mark in full and the mark stays), while heat is its own track with
 * its own cascade. So burn sits here with the consequence pips, away from the HEAT bar, the way the
 * CLI prints ` <n> burn` after the tracks rather than beside `heat`. Both are absent at 0.
 */
function PipsRow({ structure, stress, repair, shield, burn, pools }: { structure?: TrackValue; stress?: TrackValue; repair?: TrackValue; shield: number; burn: number; pools: readonly ResourcePool[] }) {
  if (!structure && !stress && !repair && shield <= 0 && burn <= 0 && pools.length === 0) return null;
  return (
    <div className="unit-sheet-pips">
      {structure && <PipGroup label={MECH_TRACK_LABELS.structure.label} term={MECH_TRACK_LABELS.structure.term} aria="structure" track={structure} />}
      {stress && <PipGroup label={MECH_TRACK_LABELS.stress.label} term={MECH_TRACK_LABELS.stress.term} aria="stress" track={stress} />}
      {repair && (
        <span className="unit-sheet-pip-group">
          <span className="unit-sheet-label">
            <Term k={MECH_TRACK_LABELS.repair.term}>{MECH_TRACK_LABELS.repair.label}</Term>
          </span>
          <span className="unit-sheet-repair" aria-label={`repair cap ${repair.value} of ${repair.max}`}>
            {repair.value} <small>/ {repair.max}</small>
          </span>
        </span>
      )}
      {shield > 0 && (
        <span className="unit-sheet-chip is-shield" aria-label={`overshield ${shield}`}>
          OVERSHIELD {shield}
        </span>
      )}
      {burn > 0 && (
        <span
          className="unit-sheet-chip is-burn"
          aria-label={`burn ${burn} marked`}
          title="Marked burn — at the end of this unit's turn an ENGINEERING check vs 10 clears it, or it deals this much damage and stays marked"
        >
          BURN {burn}
        </span>
      )}
      {pools.map((p) => (
        <span key={p.label} className="unit-sheet-chip is-pool" aria-label={`${p.label.toLowerCase()} ${p.value} of ${p.max}`} title={p.title}>
          {p.label} {p.value}/{p.max}
        </span>
      ))}
    </div>
  );
}

function PipGroup({ label, term, aria, track }: { label: string; term: GlossaryKey; aria: string; track: TrackValue }) {
  return (
    <span className="unit-sheet-pip-group">
      <span className="unit-sheet-label">
        <Term k={term}>{label}</Term>
      </span>
      <span className="unit-sheet-pip-list" role="img" aria-label={`${aria} ${track.value} of ${track.max}`}>
        {Array.from({ length: track.max }, (_, i) => (
          <span key={i} className={`unit-sheet-pip${i < track.value ? " is-on" : ""}`} />
        ))}
      </span>
    </span>
  );
}
